import { CheckCircle, XCircle, Clock, Loader2, AlertCircle, User, Mail, Lock, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { generateSecurePassword } from '@/utils/passwordGenerator';

export type StageStatus = 'pending' | 'in_progress' | 'success' | 'failed' | 'skipped';

export interface CreationStage {
  id: string;
  label: string;
  description: string;
  status: StageStatus;
  error?: string;
  details?: string;
}

export interface SeedingConfig {
  full_name: string;
  admin_email: string;
  admin_password: string;
  clear_data_first: boolean;
  copy_templates: boolean;
}

interface TenantCreationStagesModalProps {
  isOpen: boolean;
  tenantName: string;
  tenantKey?: string;
  stages: CreationStage[];
  onClose?: () => void;
  canClose: boolean;
  onSeedingConfigSubmit?: (config: SeedingConfig) => void;
  showSeedingConfig?: boolean;
}

export default function TenantCreationStagesModal({
  isOpen,
  tenantName,
  tenantKey = '',
  stages,
  onClose,
  canClose,
  onSeedingConfigSubmit,
  showSeedingConfig = false
}: TenantCreationStagesModalProps) {
  const [fullName, setFullName] = useState('System User');
  const [adminEmail, setAdminEmail] = useState('systemuser@skill-mine.com');
  const [adminPassword, setAdminPassword] = useState(generateSecurePassword(16));
  const [clearData, setClearData] = useState(true);
  const [copyTemplates, setCopyTemplates] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  if (!isOpen) return null;

  const handleGeneratePassword = () => {
    const newPassword = generateSecurePassword(16);
    setAdminPassword(newPassword);
  };

  const handleSeedingSubmit = () => {
    if (!fullName.trim() || !adminEmail.trim() || !adminPassword.trim()) {
      return;
    }

    if (onSeedingConfigSubmit) {
      onSeedingConfigSubmit({
        full_name: fullName,
        admin_email: adminEmail,
        admin_password: adminPassword,
        clear_data_first: clearData,
        copy_templates: copyTemplates
      });
    }
  };

  const getStageIcon = (status: StageStatus) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'in_progress':
        return <Loader2 className="w-5 h-5 text-brand-blue-600 animate-spin" />;
      case 'skipped':
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStageColor = (status: StageStatus) => {
    switch (status) {
      case 'success':
        return 'border-green-500 bg-green-50';
      case 'failed':
        return 'border-red-500 bg-red-50';
      case 'in_progress':
        return 'border-brand-blue-500 bg-brand-blue-50';
      case 'skipped':
        return 'border-gray-300 bg-gray-50';
      default:
        return 'border-gray-300 bg-white';
    }
  };

  const allComplete = stages.every(s => s.status === 'success' || s.status === 'failed' || s.status === 'skipped');
  const hasFailures = stages.some(s => s.status === 'failed');

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Creating Tenant</h2>
            <p className="text-sm text-gray-500 mt-1">{tenantName}</p>
            {tenantKey && <code className="text-xs text-gray-400 font-mono mt-1 block">{tenantKey}</code>}
          </div>

          {/* Stages */}
          <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
            {stages.map((stage, index) => (
              <div key={stage.id}>
                {/* Render the stage */}
                <div className={`border-2 rounded-lg p-4 transition-all ${getStageColor(stage.status)}`}>
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="flex-shrink-0 mt-0.5">
                      {getStageIcon(stage.status)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-gray-500">
                          STAGE {index + 1}
                        </span>
                        {stage.status === 'in_progress' && (
                          <span className="text-xs text-brand-blue-600 font-medium animate-pulse">
                            Processing...
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {stage.label}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {stage.description}
                      </p>

                      {/* Details */}
                      {stage.details && stage.status === 'success' && (
                        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                          ✓ {stage.details}
                        </div>
                      )}

                      {/* Waiting/Pending Details */}
                      {stage.details && stage.status === 'pending' && (
                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                          ⏳ {stage.details}
                        </div>
                      )}

                      {/* In Progress Details */}
                      {stage.details && stage.status === 'in_progress' && (
                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                          🔄 {stage.details}
                        </div>
                      )}

                      {/* Error */}
                      {stage.error && stage.status === 'failed' && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                          ✗ {stage.error}
                        </div>
                      )}
                    </div>

                    {/* Status Badge */}
                    <div className="flex-shrink-0">
                      {stage.status === 'success' && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                          Done
                        </span>
                      )}
                      {stage.status === 'failed' && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                          Failed
                        </span>
                      )}
                      {stage.status === 'in_progress' && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                          Running
                        </span>
                      )}
                      {stage.status === 'skipped' && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                          Skipped
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Show Seeding Config Form AFTER Stage 3 (cloudflare_dns) */}
                {showSeedingConfig && stage.id === 'cloudflare_dns' && (
                  <div className="mt-4 border-2 border-brand-blue-500 bg-gradient-to-br from-brand-blue-50 to-brand-indigo-50 rounded-lg p-5 space-y-3">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 bg-brand-blue-600 rounded">
                        <User className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="font-bold text-gray-900">Configure Admin User</h3>
                    </div>

                    {/* Full Name */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="e.g., John Smith"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue-500 bg-white"
                      />
                    </div>

                    {/* Admin Email */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Admin Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={adminEmail}
                        onChange={(e) => setAdminEmail(e.target.value)}
                        placeholder="e.g., admin@company.com"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue-500 bg-white"
                      />
                    </div>

                    {/* Admin Password */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Admin Password <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={adminPassword}
                            onChange={(e) => setAdminPassword(e.target.value)}
                            placeholder="Enter password"
                            className={`w-full px-3 py-2 pr-9 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue-500 font-mono bg-white ${adminPassword && !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(adminPassword) ? 'border-red-400' : 'border-gray-300'}`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={handleGeneratePassword}
                          className="px-3 py-2 bg-brand-blue-600 hover:bg-brand-blue-700 text-white rounded-lg transition-colors flex items-center gap-1.5"
                          title="Generate secure password"
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                          <span className="text-xs font-medium">Gen</span>
                        </button>
                      </div>
                      {adminPassword && !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(adminPassword) && (
                        <p className="text-xs text-red-500 mt-1">
                          Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special (@$!%*?&)
                        </p>
                      )}
                    </div>

                    {/* Clear Data Checkbox */}
                    <label className="flex items-start gap-2 p-2.5 border border-gray-300 bg-white rounded-lg cursor-pointer hover:border-brand-blue-400 transition-colors">
                      <input
                        type="checkbox"
                        checked={clearData}
                        onChange={(e) => setClearData(e.target.checked)}
                        className="w-4 h-4 text-brand-blue-600 focus:ring-brand-blue-500 rounded mt-0.5"
                      />
                      <div className="flex-1">
                        <span className="text-xs font-medium text-gray-900 block">
                          Clear existing data before seeding
                        </span>
                        <span className="text-xs text-gray-500">
                          Removes all data from tenant database
                        </span>
                      </div>
                    </label>

                    {/* Copy Templates Checkbox */}
                    <label className="flex items-start gap-2 p-2.5 border border-gray-300 bg-white rounded-lg cursor-pointer hover:border-brand-blue-400 transition-colors">
                      <input
                        type="checkbox"
                        checked={copyTemplates}
                        onChange={(e) => setCopyTemplates(e.target.checked)}
                        className="w-4 h-4 text-brand-blue-600 focus:ring-brand-blue-500 rounded mt-0.5"
                      />
                      <div className="flex-1">
                        <span className="text-xs font-medium text-gray-900 block">
                          Copy templates from management database
                        </span>
                        <span className="text-xs text-gray-500">
                          Copies CertificateTemplates & EmailTemplates
                        </span>
                      </div>
                    </label>

                    {/* Continue Button */}
                    <button
                      onClick={handleSeedingSubmit}
                      disabled={!fullName.trim() || !adminEmail.trim() || !adminPassword.trim() || !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(adminPassword)}
                      className="w-full py-2.5 bg-brand-blue-600 hover:bg-brand-blue-700 text-white rounded-lg transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed text-sm shadow-sm"
                    >
                      Continue to Seed Data →
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 bg-gray-50">
            {!allComplete && !hasFailures && (
              <div className="flex items-center justify-center gap-2 text-gray-600">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm font-medium">Processing stages...</span>
              </div>
            )}

            {!allComplete && hasFailures && (
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 text-red-600 mb-3">
                  <XCircle className="w-5 h-5" />
                  <span className="text-lg font-semibold">Tenant Creation Failed</span>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Tenant creation failed at an early stage. Check the error above and try again.
                </p>
                {canClose && onClose && (
                  <button
                    onClick={onClose}
                    className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Close
                  </button>
                )}
              </div>
            )}

            {allComplete && !hasFailures && (
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 text-green-600 mb-3">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-lg font-semibold">Tenant Created Successfully!</span>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  All automation stages completed. Your tenant is ready to use.
                </p>
                {canClose && onClose && (
                  <button
                    onClick={onClose}
                    className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Done
                  </button>
                )}
              </div>
            )}

            {allComplete && hasFailures && (
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 text-orange-600 mb-3">
                  <AlertCircle className="w-5 h-5" />
                  <span className="text-lg font-semibold">Tenant Created with Warnings</span>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Tenant created but some automation steps failed. Check the errors above.
                </p>
                {canClose && onClose && (
                  <button
                    onClick={onClose}
                    className="px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Close
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
